import { getApiUrl } from './api.js'

const GUEST_IDENTITY_PATCH = Symbol.for('eInvitation.photoAlbumGuestIdentity.v2')
const DEFAULT_MAX_PHOTOS = 5

function normalizeAlbumId(value) {
  return String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
}

function getAlbumIdFromLocation() {
  const match = window.location.pathname.match(/\/e-photo-album\/([^/?#]+)/i)
  return normalizeAlbumId(match ? decodeURIComponent(match[1]) : '')
}

function storageKey(albumId) {
  return `ephoto_guest_token_${albumId}`
}

function safeStorageGet(key) {
  try {
    return window.localStorage.getItem(key) || ''
  } catch {
    return ''
  }
}

function safeStorageSet(key, value) {
  try {
    if (value) window.localStorage.setItem(key, value)
    else window.localStorage.removeItem(key)
  } catch {
    // Storage is optional; the HttpOnly cookie remains the primary session.
  }
}

function createAnonymousStatus() {
  return {
    hasGuestIdentity: false,
    nameLocked: false,
    fullName: '',
    uploadedCount: 0,
    remainingCount: DEFAULT_MAX_PHOTOS,
    maxPhotos: DEFAULT_MAX_PHOTOS,
    limitReached: false,
  }
}

function nativeInputValueSetter(input, value) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
  if (setter) setter.call(input, value)
  else input.value = value
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))
}

function isRequestInstance(value) {
  return typeof Request !== 'undefined' && value instanceof Request
}

function parseRequestUrl(input) {
  try {
    const value = typeof input === 'string' || input instanceof URL
      ? String(input)
      : input?.url
    return new URL(value, window.location.href)
  } catch {
    return null
  }
}

function readRequestMethod(input, init) {
  return String(init?.method || input?.method || 'GET').toUpperCase()
}

function isGuestAlbumApi(url, albumId) {
  if (!url || !albumId) return false
  const encodedAlbumId = encodeURIComponent(albumId)
  return (
    url.pathname.includes(`/photo-albums/${encodedAlbumId}/`) ||
    url.pathname.includes(`/photo-albums/${albumId}/`)
  )
}

function updateStatusFromPayload(state, payload) {
  if (!payload || typeof payload !== 'object') return false
  if (payload.maxPhotos === undefined && payload.remainingCount === undefined && payload.uploadedCount === undefined) {
    return false
  }

  const maxPhotos = Math.max(1, Number.parseInt(payload.maxPhotos, 10) || DEFAULT_MAX_PHOTOS)
  const uploadedCount = Math.max(0, Number.parseInt(payload.uploadedCount, 10) || 0)
  const remainingCount = Math.max(0, Number.parseInt(payload.remainingCount, 10) || 0)

  state.status = {
    hasGuestIdentity: payload.hasGuestIdentity !== undefined
      ? Boolean(payload.hasGuestIdentity)
      : Boolean(payload.nameLocked || payload.fullName || state.token),
    nameLocked: Boolean(payload.nameLocked),
    fullName: String(payload.fullName || state.status.fullName || ''),
    uploadedCount,
    remainingCount,
    maxPhotos,
    limitReached: payload.limitReached !== undefined
      ? Boolean(payload.limitReached)
      : remainingCount <= 0,
  }
  return true
}

function createPersonalLink(state) {
  if (!state.token) return ''
  const url = new URL(window.location.href)
  url.search = ''
  url.hash = ''
  url.searchParams.set('guestToken', state.token)
  return url.toString()
}

function removeGuestTokenFromVisibleUrl() {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('guestToken')) return
  url.searchParams.delete('guestToken')
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
}

function emitStatus(state) {
  window.dispatchEvent(new CustomEvent('photo-album-guest-status', {
    detail: { ...state.status, personalLink: createPersonalLink(state) },
  }))
}

function showQuotaNotice(form, message, type = 'info') {
  if (!form) return
  let notice = form.querySelector('.epa-guest-quota-notice')
  if (!notice) {
    notice = document.createElement('p')
    notice.className = 'epa-guest-quota-notice'
    form.appendChild(notice)
  }
  notice.dataset.type = type
  notice.textContent = message
  window.clearTimeout(Number(notice.dataset.timer || 0))
  notice.dataset.timer = String(window.setTimeout(() => {
    if (notice.isConnected) notice.remove()
  }, 5000))
}

function collectUploadForms(root) {
  const forms = new Set()
  if (root instanceof Element) {
    if (root.matches('.epa-upload-form')) forms.add(root)
    const closest = root.closest('.epa-upload-form')
    if (closest) forms.add(closest)
  }
  root.querySelectorAll?.('.epa-upload-form').forEach((form) => forms.add(form))
  return [...forms]
}

function bindPersonalLinkActions(panel, form, personalLink) {
  const copyButton = panel.querySelector('[data-guest-action="copy"]')
  if (copyButton) {
    copyButton.onclick = async () => {
      try {
        await navigator.clipboard.writeText(personalLink)
        showQuotaNotice(form, 'Your personal upload link was copied.', 'success')
      } catch {
        window.prompt('Copy your personal upload link:', personalLink)
      }
    }
  }

  const openButton = panel.querySelector('[data-guest-action="open"]')
  if (openButton) {
    openButton.onclick = () => {
      window.open(personalLink, '_blank', 'noopener,noreferrer')
    }
  }
}

function applyGuestUi(state, root = document) {
  collectUploadForms(root).forEach((form) => {
    const nameInput = form.querySelector('.epa-field input[type="text"]')
    const nameField = nameInput?.closest('.epa-field')
    const fileInput = form.querySelector('.epa-upload-drop input[type="file"]')
    const dropZone = fileInput?.closest('.epa-upload-drop')
    const submitButton = form.querySelector('.epa-submit-button')
    const status = state.status

    if (nameInput && status.nameLocked) {
      if (nameInput.value !== status.fullName) nativeInputValueSetter(nameInput, status.fullName)
      nameInput.disabled = true
      nameInput.readOnly = true
      nameInput.setAttribute('aria-readonly', 'true')
      nameField?.classList.add('epa-guest-name-locked')

      let lock = nameField?.querySelector('.epa-guest-name-lock')
      if (!lock && nameField) {
        lock = document.createElement('span')
        lock.className = 'epa-guest-name-lock'
        lock.textContent = '🔒 Name locked after first upload'
        nameField.appendChild(lock)
      }
    } else if (nameInput) {
      nameInput.disabled = false
      nameInput.readOnly = false
      nameInput.removeAttribute('aria-readonly')
      nameField?.classList.remove('epa-guest-name-locked')
      nameField?.querySelector('.epa-guest-name-lock')?.remove()
    }

    let panel = form.querySelector('.epa-guest-upload-status')
    if (!panel) {
      panel = document.createElement('section')
      panel.className = 'epa-guest-upload-status'
      panel.setAttribute('aria-live', 'polite')
      if (dropZone) form.insertBefore(panel, dropZone)
      else form.prepend(panel)
    }

    const percentage = Math.min(100, Math.max(0, status.uploadedCount / status.maxPhotos * 100))
    const personalLink = createPersonalLink(state)
    const remainingText = status.limitReached
      ? 'You have reached the maximum limit of 5 photos for this album.'
      : `You may upload ${status.remainingCount} more photo${status.remainingCount === 1 ? '' : 's'}.`
    const renderKey = [
      status.uploadedCount,
      status.remainingCount,
      status.maxPhotos,
      status.limitReached ? 1 : 0,
      status.nameLocked ? 1 : 0,
      personalLink ? 1 : 0,
    ].join(':')

    if (panel.dataset.renderKey !== renderKey) {
      panel.dataset.renderKey = renderKey
      panel.innerHTML = `
        <div class="epa-guest-status-heading">
          <strong>${status.uploadedCount} of ${status.maxPhotos} photos uploaded</strong>
          <span>${status.remainingCount} remaining</span>
        </div>
        <div class="epa-guest-status-track" aria-hidden="true">
          <span style="width:${percentage}%"></span>
        </div>
        <p>${remainingText}</p>
        ${status.nameLocked && personalLink ? `
          <div class="epa-guest-link-actions">
            <button type="button" data-guest-action="copy">Copy My Upload Link</button>
            <button type="button" data-guest-action="open">Open in Browser</button>
          </div>
        ` : ''}
      `
      bindPersonalLinkActions(panel, form, personalLink)
    }

    if (fileInput) fileInput.disabled = status.limitReached
    dropZone?.classList.toggle('epa-upload-limit-reached', status.limitReached)

    if (submitButton) {
      if (status.limitReached) {
        submitButton.dataset.quotaDisabled = 'true'
        submitButton.disabled = true
      } else if (submitButton.dataset.quotaDisabled === 'true') {
        delete submitButton.dataset.quotaDisabled
        submitButton.disabled = false
      }
    }
  })
}

function installFileQuotaGuard(state) {
  document.addEventListener('change', (event) => {
    const input = event.target
    if (!(input instanceof HTMLInputElement) || input.type !== 'file') return
    const form = input.closest('.epa-upload-form')
    if (!form || !String(input.accept || '').toLowerCase().includes('image')) return

    const incoming = Array.from(input.files || [])
    if (!incoming.length) return

    const alreadySelected = form.querySelectorAll('.epa-preview-item').length
    const available = Math.max(0, state.status.remainingCount - alreadySelected)

    if (available <= 0) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation?.()
      input.value = ''
      showQuotaNotice(form, 'You have no remaining photo slots.', 'error')
      return
    }

    if (incoming.length <= available) return

    const transfer = new DataTransfer()
    incoming.slice(0, available).forEach((file) => transfer.items.add(file))
    input.files = transfer.files
    showQuotaNotice(
      form,
      `Only ${available} more photo${available === 1 ? '' : 's'} can be selected. Extra files were not added.`,
      'warning'
    )
  }, true)
}

function installAuthenticatedFetch(state) {
  const nativeFetch = window.fetch.bind(window)
  state.nativeFetch = nativeFetch

  window.fetch = async (input, init = {}) => {
    const requestUrl = parseRequestUrl(input)
    const method = readRequestMethod(input, init)
    const albumApi = isGuestAlbumApi(requestUrl, state.albumId)
    const headers = new Headers(init.headers || (isRequestInstance(input) ? input.headers : undefined))

    if (albumApi && state.token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${state.token}`)
    }

    const response = await nativeFetch(input, albumApi ? {
      ...init,
      headers,
      credentials: 'include',
    } : init)

    if (albumApi && method === 'POST' && /\/uploads\/?$/.test(requestUrl.pathname)) {
      response.clone().json().then((payload) => {
        if (payload?.guestToken) {
          state.token = String(payload.guestToken)
          safeStorageSet(storageKey(state.albumId), state.token)
        }

        if (updateStatusFromPayload(state, payload)) {
          emitStatus(state)
          applyGuestUi(state)
          window.setTimeout(() => applyGuestUi(state), 80)
          window.setTimeout(() => applyGuestUi(state), 400)
        }
      }).catch(() => undefined)
    }

    return response
  }
}

async function requestJson(state, path, options = {}) {
  const headers = new Headers(options.headers || {})
  if (state.token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${state.token}`)

  const response = await state.nativeFetch(getApiUrl(path), {
    ...options,
    headers,
    credentials: 'include',
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(payload.message || 'Guest session request failed.')
    error.status = response.status
    error.code = payload.code
    error.payload = payload
    throw error
  }
  return payload
}

async function bootstrapGuestIdentity(state) {
  const url = new URL(window.location.href)
  const urlToken = String(url.searchParams.get('guestToken') || '').trim()
  const savedToken = safeStorageGet(storageKey(state.albumId))

  if (urlToken) {
    state.token = urlToken
    try {
      const payload = await requestJson(state, `/photo-albums/${encodeURIComponent(state.albumId)}/guest-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guestToken: urlToken }),
      })
      safeStorageSet(storageKey(state.albumId), urlToken)
      updateStatusFromPayload(state, { ...payload, hasGuestIdentity: true })
      removeGuestTokenFromVisibleUrl()
    } catch (error) {
      state.bootstrapError = error.message
      if (error.code === 'INVALID_GUEST_TOKEN') {
        state.token = ''
        safeStorageSet(storageKey(state.albumId), '')
        removeGuestTokenFromVisibleUrl()
      } else if (error.code === 'GUEST_IDENTITY_ALREADY_LINKED') {
        state.token = savedToken || ''
        removeGuestTokenFromVisibleUrl()
      }
    }
  } else if (savedToken) {
    state.token = savedToken
    try {
      const payload = await requestJson(state, `/photo-albums/${encodeURIComponent(state.albumId)}/upload-status`)
      updateStatusFromPayload(state, payload)
    } catch (error) {
      if (error.code === 'INVALID_GUEST_TOKEN') {
        state.token = ''
        safeStorageSet(storageKey(state.albumId), '')
      } else {
        state.bootstrapError = error.message
      }
    }
  }

  if (!state.token || !state.status.nameLocked) {
    try {
      const payload = await requestJson(state, `/photo-albums/${encodeURIComponent(state.albumId)}/upload-status`)
      updateStatusFromPayload(state, payload)
    } catch (error) {
      if (!state.bootstrapError) state.bootstrapError = error.message
    }
  }

  state.ready = true
  emitStatus(state)
  applyGuestUi(state)
  if (state.bootstrapError) {
    document.querySelectorAll('.epa-upload-form').forEach((form) => {
      showQuotaNotice(form, state.bootstrapError, 'error')
    })
  }
}

export function installPhotoAlbumGuestIdentity() {
  if (typeof window === 'undefined' || globalThis[GUEST_IDENTITY_PATCH]) return

  const albumId = getAlbumIdFromLocation()
  if (!albumId) return

  const state = {
    albumId,
    token: '',
    status: createAnonymousStatus(),
    ready: false,
    bootstrapError: '',
    nativeFetch: null,
  }

  installAuthenticatedFetch(state)
  installFileQuotaGuard(state)

  const start = () => {
    applyGuestUi(state)

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue
          applyGuestUi(state, node)
        }
      }
    })
    observer.observe(document.documentElement, { childList: true, subtree: true })

    window.addEventListener('photo-album-guest-status', () => applyGuestUi(state))
    bootstrapGuestIdentity(state)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true })
  } else {
    start()
  }

  globalThis[GUEST_IDENTITY_PATCH] = true
}
